import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ArrowLeft, ArrowRight, Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { useMutation } from '@tanstack/react-query';
import { AppHeader } from '@/components/layout/AppHeader';

const MAX_CHARS = 500;
const AUTOSAVE_KEY = 'hallpass_bio_draft';

const INCOME_RANGES = [
  { value: 'under_30k', label: 'Under $30,000' },
  { value: '30k_50k', label: '$30,000 - $50,000' },
  { value: '50k_75k', label: '$50,000 - $75,000' },
  { value: '75k_100k', label: '$75,000 - $100,000' },
  { value: '100k_150k', label: '$100,000 - $150,000' },
  { value: '150k_plus', label: '$150,000+' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
];

export const ProfileBioPage: React.FC = () => {
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const [age, setAge] = useState('');
  const [incomeRange, setIncomeRange] = useState('');
  const [bio, setBio] = useState('');
  const [lookingFor, setLookingFor] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  // Load draft from localStorage on mount
  useEffect(() => {
    const draft = localStorage.getItem(AUTOSAVE_KEY);
    if (draft) {
      try {
        const parsed = JSON.parse(draft);
        if (parsed.age) setAge(parsed.age);
        if (parsed.incomeRange) setIncomeRange(parsed.incomeRange);
        if (parsed.bio) setBio(parsed.bio);
        if (parsed.lookingFor) setLookingFor(parsed.lookingFor);
      } catch (e) {
        console.error('Failed to load draft:', e);
      }
    }
  }, []);

  // Autosave to localStorage
  useEffect(() => {
    const timer = setTimeout(() => {
      localStorage.setItem(AUTOSAVE_KEY, JSON.stringify({ age, incomeRange, bio, lookingFor }));
    }, 1000);
    return () => clearTimeout(timer);
  }, [age, incomeRange, bio, lookingFor]);

  // Save profile mutation
  const saveProfileMutation = useMutation({
    mutationFn: async (data: { age: number; incomeRange?: string; bio: string; lookingFor: string }) => {
      const token = await getToken();
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8787';
      const response = await fetch(`${apiUrl}/api/profiles/me`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error('Failed to save profile');
      return response.json();
    },
  });

  const handleSaveAndContinue = async () => {
    // Validation
    const ageNum = parseInt(age);
    if (!age || isNaN(ageNum) || ageNum < 18 || ageNum > 100) {
      toast.error('Please enter a valid age (18-100)');
      return;
    }

    if (!bio.trim()) {
      toast.error('Please write something about yourself');
      return;
    }

    if (!lookingFor.trim()) {
      toast.error('Please describe what you\'re looking for');
      return;
    }

    if (bio.length > MAX_CHARS) {
      toast.error(`Bio must be ${MAX_CHARS} characters or less`);
      return;
    }

    if (lookingFor.length > MAX_CHARS) {
      toast.error(`"Looking For" must be ${MAX_CHARS} characters or less`);
      return;
    }

    try {
      setIsProcessing(true);

      // Save profile
      await saveProfileMutation.mutateAsync({
        age: ageNum,
        incomeRange: incomeRange || undefined,
        bio,
        lookingFor
      });

      // Trigger AI processing (async, don't block on failure)
      // TODO: Re-enable once we have proper user ID mapping
      // try {
      //   await processAIMutation.mutateAsync('current-user-id');
      //   toast.success('Profile saved and analyzed!');
      // } catch (aiError) {
      //   console.error('AI processing failed:', aiError);
      //   toast.warning('Profile saved, but AI analysis failed. We\'ll retry automatically.');
      // }
      toast.success('Profile saved!');

      // Clear draft
      localStorage.removeItem(AUTOSAVE_KEY);

      // Navigate to next step
      setIsProcessing(false);
      navigate('/profile/lifestyle');

    } catch (error) {
      setIsProcessing(false);
      console.error('Save error:', error);
      toast.error('Failed to save profile. Please try again.');
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const bioCharsRemaining = MAX_CHARS - bio.length;
  const lookingForCharsRemaining = MAX_CHARS - lookingFor.length;
  const bioExceedsLimit = bio.length > MAX_CHARS;
  const lookingForExceedsLimit = lookingFor.length > MAX_CHARS;

  return (
    <>
      <AppHeader />
      <div className="min-h-screen bg-brand-background flex items-center justify-center p-4 pt-20">
        <div className="w-full max-w-2xl">
          <Card className="shadow-xl bg-brand-surface border-brand-border">
          <CardHeader className="text-center">
            <CardTitle className="text-3xl font-bold text-white">
              Tell Us About Yourself
            </CardTitle>
            <CardDescription className="text-lg mt-2 text-brand-muted">
              Share your personality and what you're looking for in a partner
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Age and Income Range Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Age Input */}
              <div className="space-y-2">
                <Label htmlFor="age" className="text-lg font-semibold text-white">
                  Age *
                </Label>
                <Input
                  id="age"
                  type="number"
                  min="18"
                  max="100"
                  value={age}
                  onChange={(e) => setAge(e.target.value)}
                  placeholder="e.g., 28"
                  className="text-base bg-brand-background border-brand-border text-white placeholder:text-gray-500"
                />
              </div>

              {/* Income Range Dropdown */}
              <div className="space-y-2">
                <Label htmlFor="incomeRange" className="text-lg font-semibold text-white">
                  Income Range (Optional)
                </Label>
                <Select value={incomeRange} onValueChange={setIncomeRange}>
                  <SelectTrigger className="bg-brand-background border-brand-border text-white">
                    <SelectValue placeholder="Select income range" />
                  </SelectTrigger>
                  <SelectContent>
                    {INCOME_RANGES.map((range) => (
                      <SelectItem key={range.value} value={range.value}>
                        {range.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* About Me Section */}
            <div className="space-y-2">
              <Label htmlFor="bio" className="text-lg font-semibold text-white">
                About Me
              </Label>
              <Textarea
                id="bio"
                value={bio}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setBio(e.target.value)}
                placeholder="Tell us about yourself... What do you love to do? What makes you unique? What's important to you?"
                className={`min-h-[150px] text-base bg-brand-background border-brand-border text-white placeholder:text-gray-500 ${bioExceedsLimit ? 'border-red-500 focus:ring-red-500' : ''}`}
                maxLength={MAX_CHARS + 50} // Allow slight overflow for UX
              />
              <div className="flex items-center justify-between text-sm">
                <p className={`${bioExceedsLimit ? 'text-red-400 font-semibold' : 'text-brand-muted'}`}>
                  {bio.length}/{MAX_CHARS} characters
                </p>
                {bioExceedsLimit && (
                  <p className="text-red-400 text-xs">
                    Too long! Remove {Math.abs(bioCharsRemaining)} characters
                  </p>
                )}
              </div>
            </div>

            {/* Looking For Section */}
            <div className="space-y-2">
              <Label htmlFor="lookingFor" className="text-lg font-semibold text-white">
                What I'm Looking For
              </Label>
              <Textarea
                id="lookingFor"
                value={lookingFor}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setLookingFor(e.target.value)}
                placeholder="Describe your ideal partner and relationship... What qualities matter most? What are you hoping to find?"
                className={`min-h-[150px] text-base bg-brand-background border-brand-border text-white placeholder:text-gray-500 ${lookingForExceedsLimit ? 'border-red-500 focus:ring-red-500' : ''}`}
                maxLength={MAX_CHARS + 50}
              />
              <div className="flex items-center justify-between text-sm">
                <p className={`${lookingForExceedsLimit ? 'text-red-400 font-semibold' : 'text-brand-muted'}`}>
                  {lookingFor.length}/{MAX_CHARS} characters
                </p>
                {lookingForExceedsLimit && (
                  <p className="text-red-400 text-xs">
                    Too long! Remove {Math.abs(lookingForCharsRemaining)} characters
                  </p>
                )}
              </div>
            </div>

            {/* AI Helper Text */}
            <div className="flex items-start gap-2 p-3 bg-purple-500/10 border border-purple-500/20 rounded-lg">
              <Sparkles className="h-5 w-5 text-purple-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-purple-200">
                <strong>AI Matching:</strong> Our AI will analyze your writing to understand your personality,
                communication style, and what matters most to you—helping us find truly compatible matches.
              </p>
            </div>

            {/* Privacy Reminder */}
            <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <p className="text-sm text-blue-200">
                <strong>Privacy Tip:</strong> Avoid mentioning your school name, district, full name,
                or other identifying details. Keep it personal but private!
              </p>
            </div>

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between pt-4">
              <Button
                onClick={handleBack}
                variant="ghost"
                className="flex items-center gap-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-full px-6"
                disabled={isProcessing}
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>

              <Button
                onClick={handleSaveAndContinue}
                disabled={!age || !bio.trim() || !lookingFor.trim() || bioExceedsLimit || lookingForExceedsLimit || isProcessing}
                className="bg-brand-primary hover:bg-brand-primary/90 hover:scale-105 text-white flex items-center gap-2 px-8 rounded-full shadow-lg shadow-brand-primary/20 transition-transform"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    Save & Continue
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Helper Text */}
        <div className="mt-6 text-center space-y-2">
          <p className="text-sm text-gray-400">
            <strong className="text-white">Writing Tips:</strong> Be authentic, specific, and positive. Share your genuine interests and values.
          </p>
          </div>
        </div>
      </div>

      {/* AI Processing Modal */}
        <Dialog open={isProcessing} onOpenChange={() => {}}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-purple-600" />
                Analyzing Your Profile
              </DialogTitle>
              <DialogDescription className="pt-4">
                <div className="flex flex-col items-center gap-4">
                  <Loader2 className="h-12 w-12 animate-spin text-purple-600" />
                  <p className="text-center text-sm">
                    Our AI is analyzing your profile to understand your personality and find compatible matches.
                    This usually takes just a few seconds...
                  </p>
                </div>
              </DialogDescription>
            </DialogHeader>
          </DialogContent>
        </Dialog>
    </>
  );
};
